/*
 * In-browser admin Shell. Mirrors the spring-shell CLI: the user types e.g. `system ping` or
 * `domain get all --sortDirection desc`, which is parsed against the generated manifest and
 * executed against the existing REST API using the current session (no extra authentication and
 * no new backend endpoint). JSON responses are colour-coded.
 */
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageContent} from "components/src/layout/PageContent";
import {PageHeader} from "components/src/layout/PageHeader";
import {Terminal as ShellIcon} from "iconoir-react";
import {KeyboardEvent, useEffect, useRef, useState} from "react";
import {JsonHighlight} from "./JsonHighlight";
import {unsupportedReason} from "./ShellManifest";
import {execute} from "./ShellExecutor";
import {postProcessorFor} from "./ShellPostProcess";
import {autocomplete, helpCommand, helpOverview} from "./ShellHelp";
import {parse, tokenize} from "./ShellParser";
import "./Shell.scss";

const PROMPT = "CipherMail:>";

type EntryKind = "input" | "text" | "json" | "error";

interface Entry {
    kind: EntryKind;
    text?: string;
    json?: unknown;
}

export const Shell = () => {
    const {t} = useTranslationWithOverride();
    const {setSpinnerStateCounter} = useSpinnerStatusContext();

    const [entries, setEntries] = useState<Entry[]>([
        {kind: "text", text: "Type `help` for a list of commands, `help <command>` for details, `clear` to reset."},
    ]);
    const [input, setInput] = useState("");
    const history = useRef<string[]>([]);
    const historyIdx = useRef<number>(-1);
    const bottom = useRef<HTMLDivElement>(null);
    const fileInput = useRef<HTMLInputElement>(null);

    // open a native file picker and resolve with the chosen local file (or null if cancelled)
    function pickFile(): Promise<File | null> {
        return new Promise(resolve => {
            const input = fileInput.current;
            if (!input) {
                resolve(null);
                return;
            }
            let settled = false;
            const finish = (f: File | null) => {
                if (settled) {
                    return;
                }
                settled = true;
                input.removeEventListener("change", onChange);
                window.removeEventListener("focus", onFocus, true);
                resolve(f);
            };
            const onChange = () => finish(input.files?.[0] ?? null);
            // focus returns to the window when the dialog closes; covers the cancel case
            const onFocus = () => window.setTimeout(() => finish(input.files?.[0] ?? null), 350);
            input.value = "";
            input.addEventListener("change", onChange);
            window.addEventListener("focus", onFocus, true);
            input.click();
        });
    }

    useEffect(() => {
        bottom.current?.scrollIntoView({behavior: "auto"});
    }, [entries]);

    const append = (...e: Entry[]) => setEntries(prev => [...prev, ...e]);

    async function run(line: string) {
        const trimmed = line.trim();
        if (trimmed === "") {
            return;
        }
        history.current.push(trimmed);
        historyIdx.current = history.current.length;
        append({kind: "input", text: trimmed});

        if (trimmed === "clear") {
            setEntries([]);
            return;
        }
        if (trimmed === "help") {
            append({kind: "text", text: helpOverview()});
            return;
        }
        if (trimmed.startsWith("help ")) {
            append({kind: "text", text: helpCommand(tokenize(trimmed).slice(1))});
            return;
        }

        const result = parse(trimmed);
        if (!result.command) {
            append({kind: "error", text: result.error ?? "unknown command"});
            return;
        }
        if (result.command.unsupported) {
            append({kind: "error", text: `${result.command.words.join(" ")} ${unsupportedReason(result.command.unsupported)}.`});
            return;
        }
        if (result.command.clientLogic && !postProcessorFor(result.command.words)) {
            append({kind: "error", text: `${result.command.words.join(" ")} performs client-side processing and is not available in the web shell.`});
            return;
        }
        if (result.error) {
            append({kind: "error", text: result.error});
            return;
        }

        if (setSpinnerStateCounter) {
            setSpinnerStateCounter(c => c + 1);
        }
        try {
            const res = await execute(result.command, result.values, pickFile);
            if (res.error) {
                append({kind: "error", text: res.error});
            } else if (res.json !== undefined) {
                if (!res.ok) {
                    append({kind: "error", text: `HTTP ${res.status}`});
                }
                append({kind: "json", json: res.json});
            } else {
                const body = res.text && res.text.length > 0 ? res.text : "(no content)";
                append({kind: res.ok ? "text" : "error", text: res.ok ? body : `HTTP ${res.status}: ${body}`});
            }
        } finally {
            if (setSpinnerStateCounter) {
                setSpinnerStateCounter(c => c - 1);
            }
        }
    }

    function applyCompletion() {
        const {completion, listing} = autocomplete(input);
        if (completion !== null) {
            setInput(completion);
        } else if (listing.length > 0) {
            append({kind: "text", text: listing.join("   ")});
        }
    }

    function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            const line = input;
            setInput("");
            void run(line);
            return;
        }
        if (e.key === "Tab") {
            e.preventDefault();
            applyCompletion();
            return;
        }
        // history navigation only when the input is a single line
        if ((e.key === "ArrowUp" || e.key === "ArrowDown") && !input.includes("\n")) {
            if (history.current.length === 0) {
                return;
            }
            e.preventDefault();
            if (e.key === "ArrowUp") {
                historyIdx.current = Math.max(0, historyIdx.current - 1);
            } else {
                historyIdx.current = Math.min(history.current.length, historyIdx.current + 1);
            }
            setInput(history.current[historyIdx.current] ?? "");
        }
    }

    return (
        <>
            <PageHeader icon={<ShellIcon/>} title={t("shell.title")}/>
            <PageContent>
                <div className="shell" onClick={() => {
                    // don't steal focus (which would clear the selection) when the user is
                    // selecting text to copy; only focus the input on a plain click
                    if (window.getSelection()?.toString()) {
                        return;
                    }
                    document.getElementById("shell-input")?.focus();
                }}>
                    <input ref={fileInput} type="file" style={{display: "none"}}/>
                    <div className="shell-output">
                        {entries.map((e, i) => (
                            <div key={i} className={`shell-entry shell-entry-${e.kind}`}>
                                {e.kind === "input" && <span className="shell-prompt">{PROMPT} </span>}
                                {e.kind === "json"
                                    ? <JsonHighlight value={e.json}/>
                                    : <span className="shell-line">{e.text}</span>}
                            </div>
                        ))}
                        <div ref={bottom}/>
                    </div>
                    <div className="shell-inputline">
                        <span className="shell-prompt">{PROMPT}</span>
                        <textarea
                            id="shell-input"
                            className="shell-textarea"
                            value={input}
                            spellCheck={false}
                            autoComplete="off"
                            autoFocus
                            rows={Math.min(8, input.split("\n").length)}
                            placeholder={t("shell.placeholder")}
                            onChange={ev => setInput(ev.target.value)}
                            onKeyDown={onKeyDown}
                        />
                    </div>
                </div>
            </PageContent>
        </>
    );
};
